package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextAlign
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppPageIndicator
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.BusinessCover
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.KeepScreenOn
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.Language
import diana.soleil.design.iraniancanadienbazzar.core.model.Recipe
import diana.soleil.design.iraniancanadienbazzar.core.model.formatCount
import diana.soleil.design.iraniancanadienbazzar.core.model.localizeDigits
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_close_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_finish
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_image_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_keep_awake
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_next
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_prev
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_step_counter
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_recipe_not_found
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_timer_add1
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_timer_add1_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_timer_add5
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_timer_add5_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_timer_done
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_timer_label
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_timer_pause
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_timer_reset_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_cook_timer_start
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

/** Stateful entry for Cook Mode (`/gems/recipe/{id}/cook`). */
@Composable
fun CookModeRoute(
    recipeId: String,
    onClose: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: RecipeDetailViewModel = koinViewModel(parameters = { parametersOf(recipeId) }),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    when {
        state.loading -> LoadingState(modifier = modifier.fillMaxSize())
        state.recipe == null -> Column(
            modifier = modifier.fillMaxSize().background(AppTheme.colors.background).statusBarsPadding().navigationBarsPadding(),
        ) {
            AppIconButton(
                iconName = "close",
                contentDescription = stringResource(Res.string.gems_cook_close_a11y),
                onClick = onClose,
                tint = AppTheme.colors.onSurface,
                modifier = Modifier.padding(AppTheme.spacing.md),
            )
            EmptyState(title = stringResource(Res.string.gems_recipe_not_found))
        }
        else -> CookModeScreen(recipe = state.recipe ?: return, onClose = onClose, modifier = modifier)
    }
}

/**
 * Cook Mode — Stitch `a0906c314f454dbf919811095b67ad73`: step-by-step full-screen pager with a
 * top progress bar + "Step x of n" pill, huge step text, optional step image, oversized
 * Previous/Next paddles, progress dots, and keep-screen-on (the "hands are busy" differentiator).
 */
@Composable
fun CookModeScreen(
    recipe: Recipe,
    onClose: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val lang = AppTheme.language
    val steps = recipe.steps
    val pagerState = rememberPagerState(pageCount = { steps.size })
    val scope = rememberCoroutineScope()

    // Session kitchen timer — hoisted above the pager so it keeps counting while the cook flips
    // between steps (the long-simmer use case). One shared timer, not per-step.
    var timerSeconds by remember { mutableStateOf(0) }
    var timerRunning by remember { mutableStateOf(false) }
    var timerFinished by remember { mutableStateOf(false) }
    LaunchedEffect(timerRunning) {
        while (timerRunning) {
            delay(1000)
            timerSeconds -= 1
            if (timerSeconds <= 0) {
                timerSeconds = 0
                timerRunning = false
                timerFinished = true
            }
        }
    }

    // The whole point of Cook Mode: the display must not sleep mid-step.
    KeepScreenOn()

    Column(
        modifier = modifier
            .fillMaxSize()
            .background(AppTheme.colors.background)
            .statusBarsPadding().navigationBarsPadding(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // ── Top bar: close, recipe title, step pill.
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = AppTheme.spacing.md, vertical = AppTheme.spacing.sm),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            AppIconButton(
                iconName = "close",
                contentDescription = stringResource(Res.string.gems_cook_close_a11y),
                onClick = onClose,
                tint = AppTheme.colors.onSurface,
            )
            Text(
                text = recipe.title.resolve(lang),
                style = AppTheme.typography.labelMd,
                color = AppTheme.colors.onSurfaceVariant,
                textAlign = TextAlign.Center,
                modifier = Modifier.weight(1f),
            )
            Text(
                text = stringResource(
                    Res.string.gems_cook_step_counter,
                    lang.formatCount(pagerState.currentPage + 1),
                    lang.formatCount(steps.size),
                ),
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onPrimaryContainer,
                modifier = Modifier
                    .background(AppTheme.colors.primaryContainer, AppTheme.shapes.pill)
                    .padding(horizontal = AppTheme.spacing.md, vertical = AppTheme.spacing.xs),
            )
        }
        // ── Progress: filled bar tracking the current step.
        LinearProgressIndicator(
            progress = {
                if (steps.isEmpty()) 0f else (pagerState.currentPage + 1) / steps.size.toFloat()
            },
            color = AppTheme.colors.primary,
            trackColor = AppTheme.colors.surfaceContainerHigh,
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = AppTheme.spacing.lg),
        )

        // ── Step pager: huge text + optional photo.
        HorizontalPager(
            state = pagerState,
            modifier = Modifier.weight(1f),
        ) { page ->
            val step = steps[page]
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(AppTheme.spacing.xl),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xl),
            ) {
                Text(
                    text = step.text.resolve(lang),
                    style = AppTheme.typography.headlineLg,
                    color = AppTheme.colors.onSurface,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.widthIn(max = AppTheme.sizes.readingMaxWidth),
                )
                step.imageUrl?.takeIf { it.isNotBlank() }?.let { url ->
                    Box(
                        modifier = Modifier
                            .widthIn(max = AppTheme.sizes.contentMaxWidthCompact)
                            .fillMaxWidth()
                            .aspectRatio(STEP_IMAGE_ASPECT_RATIO)
                            .clip(AppTheme.shapes.card),
                    ) {
                        BusinessCover(
                            imageUrl = url,
                            iconName = "skillet",
                            contentDescription = stringResource(Res.string.gems_cook_image_a11y),
                        )
                    }
                }
            }
        }

        // ── Progress dots.
        AppPageIndicator(
            pageCount = steps.size,
            currentPage = pagerState.currentPage,
            modifier = Modifier.padding(vertical = AppTheme.spacing.md),
        )

        // ── Session kitchen timer (hands-busy simmer helper).
        CookTimer(
            seconds = timerSeconds,
            running = timerRunning,
            finished = timerFinished,
            lang = lang,
            onAddMinutes = { minutes ->
                timerSeconds += minutes * 60
                timerFinished = false
            },
            onToggle = {
                if (timerSeconds > 0) {
                    timerRunning = !timerRunning
                    timerFinished = false
                }
            },
            onReset = {
                timerRunning = false
                timerSeconds = 0
                timerFinished = false
            },
            modifier = Modifier
                .widthIn(max = AppTheme.sizes.contentMaxWidthCompact)
                .fillMaxWidth()
                .padding(horizontal = AppTheme.spacing.lg)
                .padding(bottom = AppTheme.spacing.sm),
        )

        // ── Paddles: oversized Previous / Next (start/end — mirrors in RTL).
        Row(
            modifier = Modifier
                .widthIn(max = AppTheme.sizes.contentMaxWidthCompact)
                .fillMaxWidth()
                .padding(horizontal = AppTheme.spacing.lg)
                .padding(bottom = AppTheme.spacing.md),
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            AppButton(
                text = stringResource(Res.string.gems_cook_prev),
                onClick = {
                    scope.launch {
                        pagerState.animateScrollToPage((pagerState.currentPage - 1).coerceAtLeast(0))
                    }
                },
                variant = AppButtonVariant.Secondary,
                enabled = pagerState.currentPage > 0,
                modifier = Modifier
                    .weight(1f)
                    .heightIn(min = AppTheme.sizes.cookModePaddle),
            )
            val lastStep = pagerState.currentPage >= steps.size - 1
            AppButton(
                text = stringResource(if (lastStep) Res.string.gems_cook_finish else Res.string.gems_cook_next),
                onClick = {
                    if (lastStep) {
                        onClose()
                    } else {
                        scope.launch { pagerState.animateScrollToPage(pagerState.currentPage + 1) }
                    }
                },
                trailingIcon = if (lastStep) null else "arrow_forward",
                modifier = Modifier
                    .weight(1f)
                    .heightIn(min = AppTheme.sizes.cookModePaddle),
            )
        }

        // ── Keep-awake caption (Stitch: "Screen stays on").
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
            modifier = Modifier.padding(bottom = AppTheme.spacing.lg),
        ) {
            AppIcon(
                name = "visibility",
                size = AppTheme.sizes.iconSm,
                tint = AppTheme.colors.onSurfaceVariant,
            )
            Text(
                text = stringResource(Res.string.gems_cook_keep_awake),
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onSurfaceVariant,
            )
        }
    }
}

/**
 * Session kitchen timer: a compact MM:SS display with +1/+5-minute chips, a Start/Pause toggle, and
 * a reset. Lives above the paddles and keeps counting while the cook flips between steps. On zero it
 * flips to a highlighted "time is up" state until reset or re-armed.
 */
@Composable
private fun CookTimer(
    seconds: Int,
    running: Boolean,
    finished: Boolean,
    lang: Language,
    onAddMinutes: (Int) -> Unit,
    onToggle: () -> Unit,
    onReset: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val accent = if (finished) AppTheme.colors.secondary else AppTheme.colors.onSurface
    // Two stacked rows (display over controls) rather than one wide row: keeps long localized labels
    // ("Démarrer", Farsi) from overflowing/clipping on narrow phones.
    Column(
        modifier = modifier
            .clip(AppTheme.shapes.card)
            .background(AppTheme.colors.surfaceContainer)
            .padding(horizontal = AppTheme.spacing.md, vertical = AppTheme.spacing.sm),
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            AppIcon(name = "timer", size = AppTheme.sizes.iconSm, tint = accent)
            Text(
                text = stringResource(Res.string.gems_cook_timer_label),
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onSurfaceVariant,
                modifier = Modifier.weight(1f),
            )
            Text(
                text = if (finished) stringResource(Res.string.gems_cook_timer_done) else formatTimer(seconds, lang),
                style = AppTheme.typography.titleSm,
                color = accent,
            )
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            val add1Label = stringResource(Res.string.gems_cook_timer_add1_a11y)
            val add5Label = stringResource(Res.string.gems_cook_timer_add5_a11y)
            AppChip(
                label = stringResource(Res.string.gems_cook_timer_add1),
                onClick = { onAddMinutes(1) },
                modifier = Modifier.semantics { contentDescription = add1Label },
            )
            AppChip(
                label = stringResource(Res.string.gems_cook_timer_add5),
                onClick = { onAddMinutes(5) },
                modifier = Modifier.semantics { contentDescription = add5Label },
            )
            AppButton(
                text = stringResource(if (running) Res.string.gems_cook_timer_pause else Res.string.gems_cook_timer_start),
                onClick = onToggle,
                enabled = seconds > 0,
                variant = AppButtonVariant.Secondary,
                leadingIcon = if (running) "pause" else "play_arrow",
                modifier = Modifier.weight(1f),
            )
            AppIconButton(
                iconName = "restart_alt",
                contentDescription = stringResource(Res.string.gems_cook_timer_reset_a11y),
                onClick = onReset,
                tint = AppTheme.colors.onSurfaceVariant,
            )
        }
    }
}

/** Whole minutes:seconds with the active language's digits, e.g. `12:05` / `۱۲:۰۵`. */
private fun formatTimer(totalSeconds: Int, lang: Language): String {
    val minutes = totalSeconds / 60
    val secs = totalSeconds % 60
    return lang.localizeDigits("$minutes:${secs.toString().padStart(2, '0')}")
}

/** Step photo aspect (matches the Stitch card). */
private const val STEP_IMAGE_ASPECT_RATIO = 16f / 10f
